/**
 * Display helpers for priced offer lines (net/VAT/gross from backend).
 */

/**
 * @param {object} offer
 * @returns {boolean}
 */
export function shouldShowLinePrices(offer) {
  return !offer?.displayMode || offer.displayMode === 'exact';
}

/**
 * @param {object} offer
 * @returns {boolean}
 */
export function shouldShowTotals(offer) {
  return offer?.displayMode !== 'hidden';
}

/**
 * @param {object} offer
 * @returns {boolean}
 */
export function shouldShowSubtotals(offer) {
  return !offer?.displayMode || offer.displayMode === 'exact';
}

/**
 * @param {object} offer
 * @returns {string|null}
 */
export function formatOfferTotalText(offer) {
  if (!offer || offer.displayMode === 'hidden') {
    return null;
  }

  if (offer.displayMode === 'range') {
    if (typeof offer.totalAmount === 'string') {
      return offer.totalAmount;
    }
    if (offer.priceRange) {
      const { low, high, currency } = offer.priceRange;
      return `${currency || 'EUR'} ${Number(low).toFixed(2)} – ${Number(high).toFixed(2)}`;
    }
  }

  const { totalGross } = getOfferTotals(offer);
  if (totalGross != null) {
    return `${offer.currency || 'EUR'} ${Number(totalGross).toFixed(2)}`;
  }

  if (typeof offer.totalAmount === 'string') {
    return offer.totalAmount;
  }

  return null;
}

/**
 * Format a numeric amount or offer total for customer portal lists.
 * @param {number|string|null|undefined} amount
 * @param {object} [offer]
 * @returns {string|null}
 */
export function formatDisplayAmount(amount, offer) {
  if (offer?.displayMode === 'hidden') {
    return null;
  }

  if (!amount && amount !== 0) {
    return null;
  }

  if (typeof amount === 'string') {
    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed) || amount.includes('–')) {
      return amount;
    }
    return `€${parsed.toFixed(2)}`;
  }

  if (typeof amount === 'number') {
    return `€${amount.toFixed(2)}`;
  }

  return null;
}

/**
 * @param {object} service
 * @returns {number}
 */
export function getServiceLineGross(service) {
  if (service?.lineGross != null) {
    return typeof service.lineGross === 'number'
      ? service.lineGross
      : parseFloat(service.lineGross) || 0;
  }
  const qty = parseFloat(service?.quantity);
  const quantity = Number.isFinite(qty) && qty > 0 ? qty : 1;
  const price = parseFloat(service?.price) || 0;
  const net = price * quantity;
  const vatRate = service?.vat != null ? parseFloat(service.vat) : 0.19;
  return Math.round(net * (1 + (Number.isFinite(vatRate) ? vatRate : 0.19)) * 100) / 100;
}

/**
 * @param {object} service
 * @returns {number}
 */
export function getServiceLineNet(service) {
  if (service?.lineNet != null) {
    return typeof service.lineNet === 'number' ? service.lineNet : parseFloat(service.lineNet) || 0;
  }
  const qty = parseFloat(service?.quantity);
  const quantity = Number.isFinite(qty) && qty > 0 ? qty : 1;
  return Math.round((parseFloat(service?.price) || 0) * quantity * 100) / 100;
}

/**
 * @param {object} offer
 * @returns {{ subtotalNet: number|null, totalVat: number|null, totalGross: number|null }}
 */
/**
 * Sale percent stored on the offer, or on customerInfo for saved offers.
 * @param {object} offer
 * @returns {number}
 */
export function getOfferSalePercent(offer) {
  const candidates = [offer?.salePercent, offer?.customerInfo?.salePercent];
  for (const value of candidates) {
    const n = parseFloat(value);
    if (Number.isFinite(n) && n > 0 && n <= 100) return n;
  }
  return 0;
}

/**
 * @param {number} value
 * @returns {string}
 */
export function formatSalePercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return String(Math.round(n * 100) / 100);
}

export function getOfferTotals(offer) {
  if (!offer) return { subtotalNet: null, totalVat: null, totalGross: null };
  const subtotalNet =
    offer.subtotalNet != null
      ? offer.subtotalNet
      : offer.customerInfo?.offerSubtotalNet != null
        ? offer.customerInfo.offerSubtotalNet
        : null;
  const totalVat =
    offer.totalVat != null
      ? offer.totalVat
      : offer.customerInfo?.offerTotalVat != null
        ? offer.customerInfo.offerTotalVat
        : null;
  const totalGross =
    offer.totalAmount != null ? parseFloat(offer.totalAmount) : null;
  return { subtotalNet, totalVat, totalGross };
}
